import { CTAButton } from "@/components/Button";
import {
  raiseClubImportOverlay,
  useLaunchClubImport,
} from "@/features/MyClub/EmptyClub/useStartClubImport";
import { selectClubImportState } from "@/redux/clubImport/clubImportSlice";
import { AppDispatch } from "@/redux/store";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { ExtensionOutdatedBanner } from "../ExtensionOutdatedBanner";

/** Names this screen in the funnel; the launch and the overlay's Done share it. */
const SUBSECTION = "Mobile Guide" as const;

interface StepCardProps {
  stepLetter: string;
  description: ReactNode;
  isOptional?: boolean;
}

const StepCard = ({ stepLetter, description, isOptional }: StepCardProps) => {
  const { t } = useTranslation("clubImport");
  return (
    <div className="bg-panel-background border border-new-gray-700 rounded-[15px] p-4 flex flex-col gap-2.5">
      <div className="flex items-center gap-2.5">
        <h3 className="text-white text-sm font-bold">
          {t("step", { n: stepLetter })}
        </h3>
        {isOptional && (
          <span className="text-dark-secondary text-sm">
            {t("mobile.optional")}
          </span>
        )}
      </div>
      <p className="text-white text-sm leading-5">{description}</p>
    </div>
  );
};

export const ClubImportGuide = () => {
  const { t } = useTranslation("clubImport");
  const dispatch = useDispatch<AppDispatch>();
  const { extensionEnabledStatus, hasEaAccess } = useSelector(
    selectClubImportState,
  );
  const navigate = useNavigate();
  const launchClubImport = useLaunchClubImport({
    section: "Club Import",
    subsection: SUBSECTION,
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (extensionEnabledStatus === "disabled") {
      navigate("/club-import-tutorial");
    }
  }, [extensionEnabledStatus]);

  /*
    useLaunchClubImport, not useStartClubImport: this guide is part of the flow
    the rule routes to. The overlay owns everything after the launch.
  */
  const handleStartImport = async () => {
    // import_club is fired inside the launch, once the EA app is actually open.
    if (await launchClubImport()) raiseClubImportOverlay(dispatch, SUBSECTION);
  };

  return (
    <div className="bg-panel-base min-h-screen px-4 py-6">
      <h1 className="text-white text-lg font-bold mb-4">
        {t("mobile.guide.title")}
      </h1>

      <div className="flex flex-col gap-[15px]">
        <ExtensionOutdatedBanner />
        <StepCard
          stepLetter="1"
          description={
            <Trans
              ns="clubImport"
              i18nKey="mobile.guide.step1"
              components={{
                hl: <span className="text-primary-blue font-bold" />,
              }}
            />
          }
        />
        <StepCard stepLetter="2" description={t("tutorial.secondPart.step2")} />
        {!hasEaAccess && (
          <StepCard stepLetter="2.5" description={t("mobile.guide.step25")} />
        )}
        <StepCard stepLetter="3" description={t("tutorial.secondPart.step3")} />
        {/* Dead until the extension answers — a launch before that can only
            end in an error. */}
        <CTAButton
          onClick={handleStartImport}
          label={t("tutorial.secondPart.startImportCta")}
          disabled={extensionEnabledStatus !== "enabled"}
        />
      </div>
    </div>
  );
};
